import * as errors from 'constant/errors';

import { Controller, useFormContext } from 'react-hook-form';
import { DynamicFieldData } from './dynamic-control-types';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker';
import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { RadioButton } from 'primereact/radiobutton';
import { InputText } from 'primereact/inputtext';
import FieldError from 'components/FieldError';
import { CountryCode } from 'libphonenumber-js/core';
import { logFormErrors, slotValueLengthValidator, validatePhoneNumber } from 'utils/chatUtils';
import { classNames } from 'primereact/utils';
import { emailRegex } from 'regex';
import { useEffect } from 'react';
import { Slot } from 'interface';

export const DynamicControl = ({
    intent,
    botDetail,
    inputType,
    fieldName,
    defaultValue,
    options = [],
    config = {},
    disabled
}: DynamicFieldData) => {
    const { register, control, formState, setValue, trigger } = useFormContext();
    logFormErrors(formState);

    const findSlotByName = (fieldName: string): Slot => {
        return intent.slots.find((slot) => slot.slotName === fieldName);
    };

    const parseCountryCodes = (commaSeparatedCountryCodes: string): CountryCode[] => {
        return commaSeparatedCountryCodes.split(',').map((country) => country.trim() as CountryCode);
    };

    useEffect(() => {
        if (defaultValue) setValue(fieldName, defaultValue);
    }, []);

    switch (inputType) {
        case 'text': {
            return (
                <Controller
                    control={control}
                    name={fieldName}
                    rules={{
                        validate: (slotValue) => {
                            return slotValueLengthValidator(intent, fieldName, slotValue);
                        }
                    }}
                    render={({ field }) => (
                        <>
                            <InputText
                                {...field}
                                {...register(fieldName, config)}
                                id={fieldName}
                                className={classNames('field--max', { 'p-invalid': formState.errors[fieldName] })}
                                style={{
                                    fontSize: `${botDetail.botSettings?.fontSize}px`,
                                    width: '100%'
                                }}
                                disabled={disabled}
                                value={field.value}
                                placeholder={'Enter value here'}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    let newValue = value.replace(/\s+/g, ' ');

                                    if (findSlotByName(fieldName)?.isTextOnly) {
                                        newValue = newValue.replace(/[^a-zA-Z\s]/g, '');
                                    }
                                    setValue(fieldName, newValue);
                                    trigger(fieldName);
                                }}
                            />
                            <FieldError formState={formState} fieldName={fieldName} />
                        </>
                    )}
                />
            );
        }
        case 'select': {
            //if (defaultValue) setValue(fieldName, defaultValue);
            return (
                <Controller
                    control={control}
                    name={fieldName}
                    render={({ field, formState }) => {
                        return (
                            <>
                                <Dropdown
                                    {...field}
                                    {...register(fieldName, config)}
                                    id={fieldName}
                                    className={classNames('field--max', 'slot-form-dropdown', {
                                        'p-invalid': formState.errors[fieldName]
                                    })}
                                    style={{
                                        fontSize: `${botDetail.botSettings?.fontSize}px`,
                                        width: '100%'
                                    }}
                                    options={options}
                                    optionLabel="label"
                                    optionValue="label"
                                    disabled={disabled}
                                    placeholder={'Select value'}
                                    value={field.value}
                                    onChange={(e) => {
                                        const selectedOption = options.find((option) => option.value === e.value);
                                        setValue(fieldName, selectedOption?.value);
                                        trigger(fieldName);
                                    }}
                                    tooltip={field.value}
                                    tooltipOptions={{
                                        className: 'slot-form-dropdown-tooltip',
                                        position: 'top',
                                        mouseTrack: true,
                                        mouseTrackTop: 15,
                                        mouseTrackLeft: 15
                                    }}
                                />
                                <FieldError formState={formState} fieldName={fieldName} />
                            </>
                        );
                    }}
                />
            );
        }
        case 'date':
            return (
                <Controller
                    control={control}
                    name={fieldName}
                    render={({ field: { onBlur, value } }) => (
                        <ReactDatePicker
                            {...register(fieldName, config)}
                            onChange={(date: any) => {
                                setValue(fieldName, date);
                                trigger(fieldName);
                            }}
                            disabled={disabled}
                            onBlur={onBlur}
                            selected={value}
                            dateFormat="dd/MM/yyyy"
                        />
                    )}
                />
            );
        case 'phone':
            return (
                <Controller
                    control={control}
                    name={fieldName}
                    rules={{
                        validate: (slotValue) => validatePhoneNumber(slotValue)
                    }}
                    render={({ field }) => {
                        const commaSeparatedCountryCodes = findSlotByName(fieldName)?.countries;
                        const countryCodes: CountryCode[] = parseCountryCodes(commaSeparatedCountryCodes);

                        return (
                            <>
                                <PhoneInput
                                    {...register(fieldName, config)}
                                    className={`field--max ${formState.errors[fieldName] && 'p-invalid'}`}
                                    international
                                    defaultCountry="US"
                                    countries={countryCodes || ['US']}
                                    value={field.value}
                                    disabled={disabled}
                                    onChange={field.onChange}
                                    placeholder={'Enter phone number'}
                                />
                                <FieldError formState={formState} fieldName={fieldName} />
                            </>
                        );
                    }}
                />
            );
        case 'number': {
            return (
                <Controller
                    control={control}
                    name={fieldName}
                    rules={{
                        validate: (slotValue) => {
                            const trimmedValue = slotValue.trim();
                            console.log('trim', !trimmedValue);

                            if (!trimmedValue) return errors.fieldRequiredError;
                            if (isNaN(Number(trimmedValue))) return errors.MustBeNumberError;

                            return slotValueLengthValidator(intent, fieldName, slotValue);
                        }
                    }}
                    render={({ field }) => (
                        <>
                            <InputText
                                {...register(fieldName)}
                                className={classNames('field--max', { 'p-invalid': formState.errors[fieldName] })}
                                style={{
                                    fontSize: `${botDetail.botSettings?.fontSize}px`,
                                    width: '100%'
                                }}
                                id={fieldName}
                                placeholder={'Enter value here'}
                                value={field.value}
                                onChange={(e) => {
                                    const newValue = e.target.value.replace(/[^0-9]/g, '');
                                    setValue(fieldName, newValue);
                                    trigger(fieldName);
                                }}
                                disabled={disabled}
                            />
                            <FieldError formState={formState} fieldName={fieldName} />
                        </>
                    )}
                />
            );
        }
        case 'checkbox': {
            return (
                <Controller
                    control={control}
                    name={fieldName}
                    render={(props) => {
                        const { field } = props;

                        const handleCheckboxChange = (e, optionValue) => {
                            optionValue = optionValue.trim();
                            let newValue = field.value?.trim() || '';

                            if (e.checked) {
                                if (newValue.split(',').some((x) => x.trim() !== optionValue.trim())) {
                                    newValue = newValue ? `${newValue}, ${optionValue}` : optionValue;
                                }
                            } else {
                                newValue = newValue
                                    .split(',')
                                    .map((value) => value.trim())
                                    .filter((value) => value !== optionValue)
                                    .join(',');
                            }

                            setValue(fieldName, newValue);
                            trigger(fieldName);
                        };

                        return (
                            <div className="checkbox-list">
                                {options.map((option, index) => {
                                    return (
                                        <div key={index} className="checkbox__item">
                                            <Checkbox
                                                inputId={`${fieldName}-${index}`}
                                                {...field}
                                                {...register(fieldName, config)}
                                                className={classNames('multiselector', {
                                                    'p-invalid': formState.errors[fieldName]
                                                })}
                                                checked={field.value
                                                    ?.split(',')
                                                    .some((x) => x.trim() === option.label.trim())}
                                                disabled={disabled}
                                                onChange={(e) => handleCheckboxChange(e, option.label)}
                                            />
                                            <label className="checkbox-label" htmlFor={`${fieldName}-${index}`}>
                                                {option.label}
                                            </label>
                                        </div>
                                    );
                                })}
                                <FieldError formState={formState} fieldName={field.name} />
                            </div>
                        );
                    }}
                />
            );
        }
        case 'email': {
            return (
                <Controller
                    control={control}
                    name={fieldName}
                    rules={{
                        required: errors.fieldRequiredError,
                        pattern: {
                            value: emailRegex,
                            message: errors.InvalidEmailAddress
                        }
                    }}
                    render={({ field }) => (
                        <>
                            <InputText
                                {...field}
                                {...register(fieldName, config)}
                                id={fieldName}
                                className={classNames('field--max', { 'p-invalid': formState.errors[fieldName] })}
                                style={{
                                    fontSize: `${botDetail.botSettings?.fontSize}px`,
                                    width: '100%'
                                }}
                                disabled={disabled}
                                placeholder={'Enter value here'}
                                onChange={(e) => {
                                    const newValue = e.target.value.replace(/\s+/g, '');
                                    setValue(fieldName, newValue);
                                    trigger(fieldName);
                                }}
                            />
                            <FieldError formState={formState} fieldName={fieldName} />
                        </>
                    )}
                />
            );
        }

        case 'radiobutton': {
            return (
                <Controller
                    control={control}
                    name={fieldName}
                    rules={config}
                    render={({ field }) => (
                        <div className="radio-list">
                            {options.map((option, index) => (
                                <div key={index} className="radio__item">
                                    <RadioButton
                                        id={`${fieldName}-${index}`}
                                        name={fieldName}
                                        value={option.value}
                                        onChange={(e) => {
                                            setValue(fieldName, e.value);
                                            trigger(fieldName);
                                        }}
                                        checked={field.value === option.value}
                                        disabled={disabled}
                                    />
                                    <label className="radio-label" htmlFor={`${fieldName}-${index}`}>
                                        {option.label}
                                    </label>
                                </div>
                            ))}
                            <FieldError formState={formState} fieldName={fieldName} />
                        </div>
                    )}
                />
            );
        }
        default:
            return <input type="text" />;
    }
};
